import { useState, useCallback, useEffect } from 'react';
import {
  fetchUtilisationSettings,
  saveUtilisationSettings,
  type UtilisationFormulaType,
} from '@/lib/utilisation-settings-storage';

/** Maps between the frontend UI values and the backend API values. */
const UI_TO_API: Record<string, UtilisationFormulaType> = {
  actual_vs_budget: 'ActualVsBudgeted',
  actual_vs_allocated: 'ActualVsAllocated',
  billable_vs_capacity: 'BillableVsCapacity',
};

const API_TO_UI: Record<string, string> = {
  ActualVsBudgeted: 'actual_vs_budget',
  ActualVsAllocated: 'actual_vs_allocated',
  BillableVsCapacity: 'billable_vs_capacity',
};

export type UIFormulaType = 'actual_vs_budget' | 'actual_vs_allocated' | 'billable_vs_capacity';

export interface UseUtilisationFormulaReturn {
  formula: UIFormulaType;
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  updateFormula: (value: UIFormulaType) => void;
  saveFormula: () => Promise<void>;
}

export function useUtilisationFormula(): UseUtilisationFormulaReturn {
  const [formula, setFormula] = useState<UIFormulaType>('actual_vs_budget');
  const [savedFormula, setSavedFormula] = useState<UIFormulaType>('actual_vs_budget');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchUtilisationSettings().then((settings) => {
      if (!cancelled) {
        const uiValue = (API_TO_UI[settings.formulaType] ?? 'actual_vs_budget') as UIFormulaType;
        setFormula(uiValue);
        setSavedFormula(uiValue);
      }
    });
    return () => { cancelled = true; };
  }, []);

  const hasUnsavedChanges = formula !== savedFormula;

  const updateFormula = useCallback((value: UIFormulaType) => {
    setFormula(value);
  }, []);

  const saveFormula = useCallback(async () => {
    setIsSaving(true);
    try {
      const apiValue = UI_TO_API[formula] ?? 'ActualVsBudgeted';
      await saveUtilisationSettings(apiValue);
      setSavedFormula(formula);
    } finally {
      setIsSaving(false);
    }
  }, [formula]);

  return {
    formula,
    hasUnsavedChanges,
    isSaving,
    updateFormula,
    saveFormula,
  };
}
